import {
  Anchor,
  Box,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  SimpleGrid,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { podatki } from '../api/odjemalec';
import { IME_APLIKACIJE } from '../ime';
import { koliko, stevilo } from '../oblika/oblika';
import { Logotip } from '../postavitev/Logotip';
import { barve } from '../tema';
import { IskanjeVozil, KarticaVozila } from './JavnaZaloga';
import { javniApi, vgrajeno, VOZILA, type Vrstni } from './javno';
import { NiPonudbe } from './NiPonudbe';
import { PostavitevPonudbe } from './Postavitev';

const VELIKOST = 24;

/**
 * Javna ponudba vseh prodajalcev, ki objavljajo vozila (/ponudba, brez prijave; povezava s strani avtopisarna.si):
 * enake kartice kot zaloga prodajalca, na vsaki še prodajalec in kraj. Strani po 24 vozil, iskanje (tudi po prodajalcu
 * in kraju), izbira prodajalca in razvrščanje so na strežniku; stanje je v URL-ju (?iskanje, prodajalec, razvrsti,
 * stran od 1), da Nazaj z vozila vrne na isto mesto. Vozilo se odpre na strani prodajalca (/ponudba/<oznaka>/<id>).
 */
export function VsaPonudba() {
  const { search } = useLocation();
  const vgrajena = vgrajeno(search);
  const [parametri, setParametri] = useSearchParams();
  const iskanoVUrl = parametri.get('iskanje') ?? '';
  const izbran = parametri.get('prodajalec');
  const vrstni = (parametri.get('razvrsti') ?? 'novo') as Vrstni;
  const stran = Math.max(0, (Number(parametri.get('stran')) || 1) - 1);
  const [iskanje, setIskanje] = useState(iskanoVUrl);
  const [zakasnjeno] = useDebouncedValue(iskanje.trim(), 300);

  /** Spremeni parametre v URL-ju (prazno izbriše); vsaka sprememba razen strani vrne na prvo stran. */
  const nastavi = (spremembe: Record<string, string | null>, novVnos = false) => {
    const p = new URLSearchParams(window.location.search);
    Object.entries(spremembe).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!('stran' in spremembe)) {
      p.delete('stran');
    }
    setParametri(p, { replace: !novVnos });
  };
  useEffect(() => {
    if (zakasnjeno !== iskanoVUrl) {
      nastavi({ iskanje: zakasnjeno || null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob spremembi vpisanega iskanja
  }, [zakasnjeno]);

  const ponudba = useQuery({
    queryKey: ['vsa-ponudba', iskanoVUrl, izbran, vrstni, stran],
    queryFn: ({ signal }) =>
      podatki(
        javniApi.GET('/api/javno/ponudba', {
          signal,
          params: {
            query: {
              iskanje: iskanoVUrl || undefined,
              prodajalec: izbran ?? undefined,
              razvrsti: vrstni,
              stran,
              velikost: VELIKOST,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    document.title = `Rabljena vozila v ponudbi – ${IME_APLIKACIJE}`;
  }, []);

  if (ponudba.isPending) {
    return (
      <Center h="100dvh">
        <Loader aria-label="Nalagam" />
      </Center>
    );
  }
  if (!ponudba.data) {
    return <NiPonudbe napaka={ponudba.error} ponovi={() => void ponudba.refetch()} />;
  }
  const { prodajalci = [], vozila = [], skupaj = 0, vsehVozil = 0 } = ponudba.data;
  const poOznaki = new Map(prodajalci.map((p) => [p.oznaka!, p]));
  const strani = Math.ceil(skupaj / VELIKOST);
  const parameter = vgrajena ? '?vgrajeno=1' : '';
  const filtrirano = !!iskanoVUrl || !!izbran;

  return (
    <PostavitevPonudbe vgrajena={vgrajena} glava={vgrajena ? undefined : <Glava />}>
      <Group justify="space-between" align="flex-end" mb="md" gap="sm">
        <div>
          <Title order={1} size="h2">
            Vozila v ponudbi
          </Title>
          <Text size="sm" c="dimmed">
            {koliko(vsehVozil, VOZILA)}
            {prodajalci.length > 0 &&
              ` · ${koliko(prodajalci.length, ['prodajalec', 'prodajalca', 'prodajalci', 'prodajalcev'])}`}
          </Text>
        </div>
        {vsehVozil > 1 && (
          <IskanjeVozil
            iskanje={iskanje}
            setIskanje={setIskanje}
            vrstni={vrstni}
            setVrstni={(v) => nastavi({ razvrsti: v === 'novo' ? null : v })}
            dodatno={
              prodajalci.length > 1 && (
                <Select
                  aria-label="Prodajalec"
                  placeholder="Vsi prodajalci"
                  data={prodajalci.map((p) => ({
                    value: p.oznaka!,
                    label: p.kraj ? `${p.naziv} (${p.kraj})` : p.naziv!,
                  }))}
                  value={izbran}
                  onChange={(v) => nastavi({ prodajalec: v })}
                  clearable
                  searchable
                  style={{ flex: '1 1 200px' }}
                />
              )
            }
          />
        )}
      </Group>
      {vozila.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {vsehVozil === 0 ? 'Trenutno ni vozil v ponudbi.' : 'Ni vozil za iskanje.'}
        </Text>
      ) : (
        <>
          {filtrirano && (
            <Text size="sm" c="dimmed" mb="sm">
              Najdenih: {koliko(skupaj, VOZILA)}
            </Text>
          )}
          <SimpleGrid
            cols={{ base: 1, xs: 2, md: 3 }}
            spacing="md"
            style={{ opacity: ponudba.isPlaceholderData ? 0.6 : 1, transition: 'opacity 150ms' }}
          >
            {vozila.map((v) => (
              <KarticaVozila
                key={`${v.prodajalec}-${v.id}`}
                oznaka={v.prodajalec!}
                vozilo={v}
                iskanje={parameter}
                prodajalec={poOznaki.get(v.prodajalec ?? '')}
              />
            ))}
          </SimpleGrid>
          {strani > 1 && (
            <Group justify="space-between" mt="lg" gap="sm">
              <Text size="sm" c="dimmed">
                {stevilo(stran * VELIKOST + 1)}–{stevilo(Math.min(skupaj, (stran + 1) * VELIKOST))} od {stevilo(skupaj)}
              </Text>
              <Pagination
                aria-label="Strani"
                total={strani}
                value={Math.min(stran + 1, strani)}
                onChange={(s) => {
                  nastavi({ stran: s > 1 ? String(s) : null }, true);
                  window.scrollTo({ top: 0 });
                }}
                siblings={1}
              />
            </Group>
          )}
        </>
      )}
    </PostavitevPonudbe>
  );
}

/** Glava z logotipom, ki vodi na predstavitveno stran. */
function Glava() {
  return (
    <Box bg={barve.ozadjeTemno} py="sm">
      <Container size="lg" px={{ base: 'sm', sm: 'md' }}>
        <Anchor
          href="https://avtopisarna.si"
          underline="never"
          display="inline-block"
          aria-label={`${IME_APLIKACIJE} – predstavitvena stran`}
        >
          <Logotip velikost={26} />
        </Anchor>
      </Container>
    </Box>
  );
}
